import React, { useState, useEffect, useCallback } from 'react';
import './HRJoiningDates.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1]?.[0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Years of service to one decimal — same formula the server uses.
function tenureOf(d) {
  if (!d) return null;
  const years = (Date.now() - new Date(d).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return years < 0 ? 0 : Math.round(years * 10) / 10;
}

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export default function HRJoiningDates() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('missing'); // missing | all
  const [drafts, setDrafts]   = useState({});        // id -> date string
  const [saving, setSaving]   = useState(null);
  const [rowError, setRowErr] = useState({});
  const [toast, setToast]     = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res  = await fetch(`${BASE}/employees/joining-dates`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load'); setRows([]); }
      else setRows(data.employees || []);
    } catch {
      setError('Network error — could not reach the server');
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const save = async (emp) => {
    const value = drafts[emp.id];
    if (!value) return;
    setSaving(emp.id);
    setRowErr(e => ({ ...e, [emp.id]: null }));
    try {
      const res = await fetch(`${BASE}/employees/${emp.id}/joining-date`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ joining_date: value }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRowErr(e => ({ ...e, [emp.id]: data.error || 'Failed to save' }));
        return;
      }
      showToast(`${emp.full_name} — joining date set to ${fmtDate(value)}`);
      setDrafts(d => { const n = { ...d }; delete n[emp.id]; return n; });
      fetchData();
    } catch {
      setRowErr(e => ({ ...e, [emp.id]: 'Server error — try again' }));
    } finally { setSaving(null); }
  };

  const missingCount = rows.filter(r => !r.joining_date).length;

  let visible = filter === 'missing' ? rows.filter(r => !r.joining_date) : rows;
  if (search.trim()) {
    const q = search.toLowerCase();
    visible = visible.filter(r =>
      (r.full_name || '').toLowerCase().includes(q) ||
      (r.employee_id || '').toLowerCase().includes(q) ||
      (r.department || '').toLowerCase().includes(q) ||
      (r.city || '').toLowerCase().includes(q)
    );
  }

  return (
    <div className="jd-root">

      {toast && <div className="jd-toast">✓ {toast}</div>}

      <div className="jd-header">
        <div>
          <h1 className="jd-title">Joining Dates</h1>
          <p className="jd-sub">
            Set the date each employee started. This is what length of service
            and probation are measured from.
          </p>
        </div>
        <button className="jd-refresh" onClick={fetchData}>↻ Refresh</button>
      </div>

      {/* Status banner */}
      {!loading && (
        missingCount > 0 ? (
          <div className="jd-banner jd-banner-warn">
            <span className="jd-banner-icon">⚠️</span>
            <div>
              <b>{missingCount} employee{missingCount > 1 ? 's have' : ' has'} no joining date.</b>
              {' '}Until it's set, their length of service can't be calculated.
              Where an employee has attendance history, the date of their first
              record is shown below as a suggestion.
            </div>
          </div>
        ) : (
          <div className="jd-banner jd-banner-ok">
            <span className="jd-banner-icon">✓</span>
            <div>Every active employee has a joining date recorded.</div>
          </div>
        )
      )}

      {error && <div className="jd-banner jd-banner-err">⚠ {error}</div>}

      {/* Controls */}
      <div className="jd-controls">
        <input
          className="jd-search"
          placeholder="Search name, ID, department, city…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="jd-tabs">
          <button className={`jd-tab ${filter === 'missing' ? 'on' : ''}`}
            onClick={() => setFilter('missing')}>
            Needs a date {missingCount > 0 && <span className="jd-tab-count">{missingCount}</span>}
          </button>
          <button className={`jd-tab ${filter === 'all' ? 'on' : ''}`}
            onClick={() => setFilter('all')}>
            All employees <span className="jd-tab-count jd-tab-count-mute">{rows.length}</span>
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="jd-loading"><div className="jd-spinner" /><p>Loading employees…</p></div>
      ) : visible.length === 0 ? (
        <div className="jd-empty">
          {filter === 'missing'
            ? '✓ Nothing to fix — every employee has a joining date'
            : 'No employees match your search'}
        </div>
      ) : (
        <div className="jd-list">
          {visible.map(emp => {
            const has     = !!emp.joining_date;
            const draft   = drafts[emp.id] ?? '';
            const err     = rowError[emp.id];
            const tenure  = tenureOf(emp.joining_date);
            const isSaving = saving === emp.id;
            return (
              <div key={emp.id} className={`jd-row ${has ? '' : 'jd-row-missing'}`}>
                <div className="jd-av" style={{ background: getAvatarColor(emp.full_name) }}>
                  {getInitials(emp.full_name)}
                </div>

                <div className="jd-info">
                  <div className="jd-name">{emp.full_name}</div>
                  <div className="jd-meta">
                    {emp.employee_id}
                    {emp.department ? ` · ${emp.department}` : ''}
                    {emp.city ? ` · ${emp.city}` : ''}
                  </div>
                </div>

                <div className="jd-current">
                  {has ? (
                    <>
                      <div className="jd-current-date">{fmtDate(emp.joining_date)}</div>
                      <div className="jd-current-tenure">
                        {tenure} year{tenure === 1 ? '' : 's'} of service
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="jd-current-none">Not set</div>
                      {emp.first_attendance && (
                        <button
                          className="jd-suggest"
                          onClick={() => setDrafts(d => ({ ...d, [emp.id]: emp.first_attendance }))}
                          title="Use the date of their first attendance record"
                        >
                          Use {fmtDate(emp.first_attendance)}
                        </button>
                      )}
                    </>
                  )}
                </div>

                <div className="jd-edit">
                  <input
                    type="date"
                    className={`jd-date-input ${err ? 'jd-date-err' : ''}`}
                    value={draft}
                    max={todayStr()}
                    onChange={e => setDrafts(d => ({ ...d, [emp.id]: e.target.value }))}
                  />
                  <button
                    className="jd-save"
                    disabled={!draft || isSaving}
                    onClick={() => save(emp)}
                  >
                    {isSaving ? 'Saving…' : has ? 'Update' : 'Set'}
                  </button>
                </div>

                {err && <div className="jd-row-error">⚠ {err}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div className="jd-foot">
        Showing {visible.length} of {rows.length} active employees ·
        {' '}Changes are recorded in the audit log
      </div>
    </div>
  );
}